import { useCallback, useMemo } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { getWatchKey } from "./useWatchedMRs";

export const MR_TAGS_STORAGE_KEY = "dashboard-mr-tags";

export const getMRTagKey = (mrOrKey) =>
	typeof mrOrKey === "string" ? mrOrKey : getWatchKey(mrOrKey);

export const normalizeMRTag = (value) =>
	String(value || "")
		.trim()
		.replace(/\s+/g, " ")
		.slice(0, 40);

const tagCompareKey = (tag) => normalizeMRTag(tag).toLowerCase();

const uniqueNormalizedTags = (tags) => {
	const seen = new Set();
	const out = [];

	for (const item of Array.isArray(tags) ? tags : []) {
		const tag = normalizeMRTag(typeof item === "string" ? item : item?.name);
		const key = tagCompareKey(tag);
		if (!tag || seen.has(key)) continue;
		seen.add(key);
		out.push(tag);
	}

	return out;
};

export const useMRTags = () => {
	const [tagsByMRKey, setTagsByMRKey] = useLocalStorage(
		MR_TAGS_STORAGE_KEY,
		{},
	);

	const getTags = useCallback(
		(mrOrKey) => uniqueNormalizedTags(tagsByMRKey?.[getMRTagKey(mrOrKey)]),
		[tagsByMRKey],
	);

	const allTags = useMemo(() => {
		const byKey = new Map();
		for (const tags of Object.values(tagsByMRKey || {})) {
			for (const tag of uniqueNormalizedTags(tags)) {
				const key = tagCompareKey(tag);
				if (!byKey.has(key)) byKey.set(key, tag);
			}
		}

		return [...byKey.values()].sort((a, b) =>
			a.localeCompare(b, undefined, { sensitivity: "base" }),
		);
	}, [tagsByMRKey]);

	const addTag = useCallback(
		(mrOrKey, rawTag) => {
			const key = getMRTagKey(mrOrKey);
			const tag = normalizeMRTag(rawTag);
			if (!key || !tag) return;

			setTagsByMRKey((prev) => {
				const nextTags = uniqueNormalizedTags(prev?.[key]);
				const compareKey = tagCompareKey(tag);
				if (nextTags.some((item) => tagCompareKey(item) === compareKey)) {
					return prev;
				}

				return {
					...(prev || {}),
					[key]: [...nextTags, tag],
				};
			});
		},
		[setTagsByMRKey],
	);

	const removeTag = useCallback(
		(mrOrKey, rawTag) => {
			const key = getMRTagKey(mrOrKey);
			const compareKey = tagCompareKey(rawTag);
			if (!key || !compareKey) return;

			setTagsByMRKey((prev) => {
				const nextTags = uniqueNormalizedTags(prev?.[key]).filter(
					(tag) => tagCompareKey(tag) !== compareKey,
				);
				if (nextTags.length === uniqueNormalizedTags(prev?.[key]).length) {
					return prev;
				}

				const next = { ...(prev || {}) };
				if (nextTags.length > 0) next[key] = nextTags;
				else delete next[key];
				return next;
			});
		},
		[setTagsByMRKey],
	);

	const hasTag = useCallback(
		(mrOrKey, rawTag) => {
			const compareKey = tagCompareKey(rawTag);
			return (
				!!compareKey &&
				getTags(mrOrKey).some((tag) => tagCompareKey(tag) === compareKey)
			);
		},
		[getTags],
	);

	return {
		tagsByMRKey,
		allTags,
		getTags,
		addTag,
		removeTag,
		hasTag,
	};
};
